import { useEffect, useState, useMemo, forwardRef  } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import DatePicker from "react-datepicker";
import { CalendarToday } from "@mui/icons-material";

import "react-datepicker/dist/react-datepicker.css";

import Header from "./Header";
import Sidebar from "./Sidebar";
import Footer from "./Footer";
import SidebarIcons from "./SidebarIcons";

import http from "../api/server";
import useGetClientId from "../hooks/useGetClientId";
import { INSPECTOR_CALENDERI_VIEW } from "../api/apiPath";

import "./styles/calendar.css";
import { useSelector } from "react-redux";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";

export default function InspectionCalendar() {
  const { inspectionRefNumber } = useParams();
  const clientObj = useGetClientId();
 const { userObj } = useSelector((state) => state.user);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hideSidebar, setHideSidebar] = useState(false);

  const [inspectorFilter, setInspectorFilter] = useState("ALL");
  const [inspectorOptions, setInspectorOptions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [statusOptions, setStatusOptions] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  /* =========================
     STORE INSPECTION REF IN LOCALSTORAGE
     ========================= */
     
  useEffect(() => {
    if (inspectionRefNumber) {
      localStorage.setItem("inspectionRefNumber", inspectionRefNumber);
    }
  }, [inspectionRefNumber]);

  /* =========================
     FETCH CALENDAR DATA
     ========================= */
  useEffect(() => {
    if (clientObj.clientId) {
      fetchCalendarData();
    }
  }, [clientObj.clientId, inspectionRefNumber]);

   /* =========================
     HELPERS
     ========================= */
      const formatDateTimeSplit = (dateTimeStr) => {
        if (!dateTimeStr) return null;

        const [date, timePart] = dateTimeStr.split("T");

        // handle seconds if present (HH:mm or HH:mm:ss)
        const [hourStr, minute] = timePart.split(":");
        let hour = parseInt(hourStr, 10);

        const ampm = hour >= 12 ? "PM" : "AM";
        hour = hour % 12 || 12; // convert 0 -> 12

        const formattedTime = `${hour}:${minute} ${ampm}`;

        return (
          <div className="event-date">
            {date}
            <br />
            {formattedTime}
          </div>
        );
      };
      const fetchCalendarData = async () => {    
        if (!clientObj.clientId) return;
        try {
          setLoading(true);

          const payload = {
            clientId: clientObj.clientId,
            RoleId: userObj?.isAdmin ? 1 : 2,
            LoggedInUserEmail: userObj?.userEmail,
            inspectionReferenceNumber: inspectionRefNumber || "",
          };
          console.log("FINAL API PAYLOAD:", payload);
          const response = await http.post(INSPECTOR_CALENDERI_VIEW, payload);
          const dataArray = response.data?.data || [];
          console.log("DATA ARRAY:", dataArray);
          if (dataArray.length === 0) {
            toast.info("No inspections found");
            setEvents([]);
            setInspectorOptions([]);
            return;
          }

          // Map API data to FullCalendar events
          const mappedEvents = dataArray.map((item) => {
          const start = new Date(item.startTime);
          const end = new Date(item.endTime);

          // Check if event spans multiple days
          const startDateOnly = start.toISOString().split('T')[0];
          const endDateOnly = end.toISOString().split('T')[0];
          const isMultiDay = startDateOnly !== endDateOnly;

          // Always use full ISO datetime strings to preserve time information
          // This ensures events show in the correct time slots in week/day views
          const eventObj = {
            // id: `${item.inspectionReferenceNumber}-${item.startTime}`,
            id: `${item.inspectionReferenceNumber}-${item.inspectorId}-${item.startTime}-${item.endTime}`,
            title: item.inspectionReferenceNumber,
            start: item.startTime, // Use original ISO datetime string
            end: item.endTime,     // Use original ISO datetime string
            allDay: false,         // Explicitly set to false to prevent all-day rendering
            extendedProps: {
              inspectors: [item.inspectorName],
              status: item.status,
              approver: item.approver || "",
              inspectionName: item.inspectionName || "",
              propertyName: item.propertyName || "",
              isMultiDay,
              originalStartTime: item.startTime,
              originalEndTime: item.endTime,
            },
          };

          return eventObj;
        });



          setEvents(mappedEvents);

          // Extract unique inspectors for dropdown filter
          const uniqueInspectors = [
            ...new Set(dataArray.map((item) => item.inspectorName).filter(Boolean)),
          ];
          setInspectorOptions(uniqueInspectors);

          const uniqueStatuses = [
            ...new Set(dataArray.map((item) => item.status).filter(Boolean)),
          ];
          setStatusOptions(uniqueStatuses);
        } catch (error) {
          console.error(error);
          toast.error(
            error.response?.data?.message || "Invalid request payload sent to server"
          );
        } finally {
          setLoading(false);
        }
      };

  /* =========================
     FILTER LOGIC
     ========================= */
  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      const eventDate = new Date(event.start);
      eventDate.setHours(0, 0, 0, 0);

      const inspectorMatch =
        inspectorFilter === "ALL" ||
        event.extendedProps.inspectors.includes(inspectorFilter);
      const statusMatch =
        statusFilter === "ALL" ||
        event.extendedProps.status === statusFilter;

      const normalizedStart = startDate ? new Date(startDate) : null;
      if (normalizedStart) normalizedStart.setHours(0, 0, 0, 0);

      const normalizedEnd = endDate ? new Date(endDate) : null;
      if (normalizedEnd) normalizedEnd.setHours(23, 59, 59, 999);

      const startMatch = !normalizedStart || eventDate >= normalizedStart;
      const endMatch = !normalizedEnd || eventDate <= normalizedEnd;

      return inspectorMatch && statusMatch && startMatch && endMatch;
    });
  }, [events, inspectorFilter, statusFilter, startDate, endDate]);

/* =========================
     Date Picker 
     ========================= */
const CalendarStartDateInput = forwardRef(({ value, onClick }, ref) => (
    <div className="custom-datepicker-input" onClick={onClick} ref={ref}>
      <input
        type="text"
        value={value}
        readOnly
        className="form-control"
        placeholder="Start Date"
      />
      <CalendarToday className="calendar-icon" style={{ fontSize: 15 }} />
    </div>
  ));
  CalendarStartDateInput.displayName = "CalendarStartDateInput";

    const CalendarEndDateInput = forwardRef(({ value, onClick }, ref) => (
      <div className="custom-datepicker-input" onClick={onClick} ref={ref}>
        <input
          type="text"
          value={value}
          readOnly
          className="form-control"
          placeholder="End Date"
        />
        <CalendarToday className="calendar-icon" style={{ fontSize: 15 }} />
      </div>
    ));
    CalendarEndDateInput.displayName = "CalendarEndDateInput";

    // Clear filter
    const isAnyFilterApplied =
        inspectorFilter !== "ALL" ||
        statusFilter !== "ALL" ||
        startDate !== null ||
        endDate !== null;

const hasPendingInspectorEvent = (date) => {
  return filteredEvents.some((event) => {
    const status = event.extendedProps?.status;
    if (status !== "Pending Inspector Acceptance") return false;

    const eventStart = new Date(event.start);
    const eventEnd = event.end ? new Date(event.end) : eventStart;

    // normalize times
    eventStart.setHours(0, 0, 0, 0);
    eventEnd.setHours(0, 0, 0, 0);

    return date >= eventStart && date <= eventEnd;
  });
};

  /* =========================
     RENDER
     ========================= */
  return (
    <>
      <Header hideSidebar={hideSidebar} setHideSidebar={setHideSidebar} />

      <div className="dashboard_body_main vh-100">
        {hideSidebar ? (
          <Sidebar setHideSidebar={setHideSidebar} hideSidebar={hideSidebar} />
        ) : (
          <SidebarIcons
            setHideSidebar={setHideSidebar}
            hideSidebar={hideSidebar}
          />
        )}

        <div className="dashboard_body py-0 w-100">
          {/* Toolbar */}
          <div className="row mt-4 mb-3">
            <div className="col-lg-4">
              <h3 className="fs-3 fw-bold m-0 text-start">
                Inspection Dashboard 
              </h3>
            </div>

            
          </div>
          <div className="row">
              <div className="col-lg-12">
                <div className="row calendar-filters">
                  {/* Inspector Filter - Only for Admin */}       
                      {/* Status Filter */}
                  <div className="col-md-2 mt-2">
                    <select
                      className="form-select custom_toolbar_control"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="ALL">All Status</option>
                      {statusOptions.sort((a, b) => a.localeCompare(b)).map((status, idx) => (
                        <option key={idx} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                  {userObj?.isAdmin && (
                    <div className="col-md-2 mt-2">
                      <select
                        className="form-select custom_toolbar_control"
                        value={inspectorFilter}
                        onChange={(e) => setInspectorFilter(e.target.value)}
                      >
                        <option value="ALL">All Inspectors</option>
                        {inspectorOptions.sort((a, b) => a.localeCompare(b)).map((inspector, idx) => (
                          <option key={idx} value={inspector}>
                            {inspector}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}            

                  {/* Start Date */}
                  <div className="col-md-2 mt-2">
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      isClearable
                      placeholderText="Start Date"
                      dateFormat="yyyy-MM-dd"
                      customInput={<CalendarStartDateInput />}
                      className="form-control w-100"
                    />
                  </div>

                  {/* End Date */}
                  <div className="col-md-2 mt-2">
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => setEndDate(date)}
                      isClearable
                      placeholderText="End Date"
                      dateFormat="yyyy-MM-dd"
                      customInput={<CalendarEndDateInput />}
                      className="form-control w-100"
                    />
                  </div>
                   {/* Reset Button */}
                  {isAnyFilterApplied && (
                      <div className="col-auto reset-button d-flex align-items-canter align-items-canter p-0">
                        <IconButton
                          tabIndex={0}
                          title="Clear Filters"
                          onClick={() => {
                            setInspectorFilter("ALL");
                            setStatusFilter("ALL");
                            setStartDate(null);
                            setEndDate(null);
                          }}
                        >
                          <ClearIcon sx={{ color: "#717186" }} fontSize="small" />
                        </IconButton>
                      </div>
                    )}
                </div>
              </div>
          </div>
          <div className="calendar-status-legend">
              <div className="legend-item">
                <span className="legend-dot scheduled"></span>
                <span>Current Scheduled</span>
              </div>

              <div className="legend-item">
                <span className="legend-dot in-progress"></span>
                <span>Past Scheduled</span>
              </div>

              <div className="legend-item">
                <span className="legend-dot rejected"></span>
                <span>Future Scheduled</span>
              </div>
            </div>
          {/* Calendar */}
          <div className="inspection-calendar-wrapper mt-4">
            {loading && <p className="loading-text">Loading calendar...</p>}
          <div className="calendar-container">
          <FullCalendar
            timeZone="local"
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            forceEventDuration={true}
            slotEventOverlap={true}
            eventOverlap={true}
            allDaySlot={false}
            eventMaxStack={false}
            dayMaxEvents={false}
            slotDuration="00:30:00"
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            expandRows={true}
            height="auto"
            contentHeight={700}
            headerToolbar={{
              left:'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay,dayGridYear, prev,today,next'
            }}
            events={filteredEvents}
            selectable={false}
            
            dayMaxEventRows={false}
            buttonText={{
              day: "Day",
              today:"Today",
              month: "Month",
              week: "Week",              
              year: "Year"
            }}
            dayCellClassNames={(arg) => {
                if (hasPendingInspectorEvent(arg.date)) {
                  return ["fc-event-start"];
                }
                return [];
              }}
            eventClassNames={(arg) => {
                const eventStart = new Date(arg.event.start);
                // normalize event date
                eventStart.setHours(0, 0, 0, 0);
                // today
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (eventStart.getTime() === today.getTime()) {
                  return ["fc-event-current"];   // current date
                }
                if (eventStart < today) {
                  return ["fc-event-past"];      // past date
                }
                if (eventStart > today) {
                  return ["fc-event-future"];    // future date
                }
                return [];
              }}
              eventContent={(arg) => {
              const { event } = arg;
              const inspectors = event.extendedProps.inspectors || [];
              const isMultiDay = event.extendedProps.isMultiDay;
              
              return (
                <div className="custom-event">
                  <div className="event-title">{event.title}</div>
                  {!isMultiDay ? (
                    <div className="event-time row">
                      <div className="col-6 d-flex flex-wrap">
                        {formatDateTimeSplit(event.extendedProps.originalStartTime)}
                      </div>
                      <div className="col-6 d-flex flex-wrap">
                        {formatDateTimeSplit(event.extendedProps.originalEndTime)}
                      </div>
                    </div>
                  ) : (
                     <div className="event-time row">
                      <div className="col-6 d-flex flex-wrap">
                        {formatDateTimeSplit(event.extendedProps.originalStartTime)}
                      </div>
                      <div className="col-6 d-flex flex-wrap  ">
                        {formatDateTimeSplit(event.extendedProps.originalEndTime)} 
                      </div>
                    </div>
                  )}
                  {/* {event.extendedProps.propertyName && (
                    <div className="event-property">
                      {event.extendedProps.propertyName}
                    </div>
                  )} */}
                  <div className="event-status mt-2">
                    <strong>{event.extendedProps.status}</strong>
                  </div>
                  {event.extendedProps.approver && (
                    <div className="event-approver">
                      Approver: {event.extendedProps.approver}
                    </div>
                  )}
                  <div className="event-inspectors">
                    {inspectors.map((ins, i) => (
                      <span key={i} className="inspector-chip">
                        {ins}
                      </span>
                    ))}
                  </div>
                </div>
              );
              }}
          />
          </div>
          </div>

          <Footer />
        </div>
      </div>
    </>
  );
}